<template>
  <v-container fluid>
    <v-card>
      <v-toolbar :dark="$vuetify.theme.dark" color="primary">
        <v-col cols="10" class="text-left">
          <v-toolbar-title class="textwithbackground--text">
            {{ $t('Список файлов общего назначения') }}
          </v-toolbar-title>
        </v-col>
        <v-col cols="2" class="text-right">
          <btn-icon-tip
              v-if="isAdmin || isOrganizer"
              icon="add"
              icon-color="secondaryiconbtn"
              :tip="$t('Добавление файла')"
              :disabled="isLoading"
              @click="openAddStaticFileDialog" />
        </v-col>
      </v-toolbar>
      <general-error v-if="!!generalError" />
      <v-row class="px-2">
        <v-col>
          <v-data-table
              :headers="headers1"
              :items="files"
              :items-per-page="itemsPerPage"
              disable-filtering
              item-key="fileId"
              single-expand
              show-expand
              :loading="isLoading"
              :locale="systemLocale">
            <template v-slot:progress>
              <v-progress-linear :active="isLoading" color="loader" indeterminate />
            </template>
            <!-- eslint-disable-next-line vue/valid-v-slot -->
            <template v-slot:item.icon="{ item }">
              <v-icon>
                {{ item.icon }}
              </v-icon>
            </template>
            <!-- eslint-disable-next-line vue/valid-v-slot -->
            <template v-slot:item.fileName="{ item }">
              <link-with-tip
                  :href="getSystemFileUrl(item.fileName)"
                  :tip="$t('Скачать файл')"
                  :text="item.fileName" />
            </template>
            <!-- eslint-disable-next-line vue/valid-v-slot -->
            <template v-slot:item.contentType="{ item }">
              {{ item.contentType }}
            </template>
            <!-- eslint-disable-next-line vue/valid-v-slot -->
            <template v-slot:item.creationDate="{ item }">
              {{ item.creationDate
                ? $moment(item.creationDate).format('DD.MM.YYYY HH:mm')
                : null }}
            </template>

            <!-- eslint-disable-next-line vue/valid-v-slot -->
            <template v-slot:item.staticFileType="{ item }">
              {{ item.staticFileType
                ? $t(STATIC_FILE_TYPE_ENUM[item.staticFileType])
                :null }}
            </template>

            <template v-slot:expanded-item="{ headers, item }">
              <td :colspan="headers.length">
                <v-row class="py-4">
                  <text-view
                      :display="item.userName"
                      :label="$t('Пользователь, загрузивший файл')" />
                </v-row>
                <v-divider />
                <v-row class="text-left py-4">
                  <btn-icon-tip
                      v-if="item.canEdit"
                      icon="edit"
                      icon-color="primaryiconbtn"
                      :tip="$t('Замена файла')"
                      :disabled="isLoading"
                      @click="openEditStaticFileDialog(item)" />
                  <btn-icon-tip
                      v-if="item.canDelete"
                      icon="delete"
                      icon-color="warning"
                      :tip="$t('Удалить')"
                      :disabled="isLoading"
                      @click="openConfirmDialog(item.fileId)" />
                  <btn-icon-tip
                      v-if="item.fileName"
                      icon="content_copy"
                      icon-color="primary"
                      :tip="$t('Копировать ссылку')"
                      @click="copyLink(item.fileName)" />
                </v-row>
              </td>
            </template>
          </v-data-table>
        </v-col>
      </v-row>
    </v-card>
    <confirm-dialog
        v-if="confirmDialog.show"
        :dialog-prop="confirmDialog"
        @close="closeDialog('confirmDialog')" />
    <add-static-file-dialog
        v-if="addStaticFileDialog.show"
        :dialog-prop="addStaticFileDialog"
        @close="closeDialog('addStaticFileDialog')" />
    <edit-static-file-dialog
        v-if="editStaticFileDialog.show"
        :dialog-prop="editStaticFileDialog"
        @close="closeDialog('editStaticFileDialog')" />
  </v-container>
</template>

<script>

  import BtnIconTip from '@/components/reused/ui/BtnIconTip.vue'
  import TextView from '@/components/reused/ui/TextView.vue'
  // import RouteBtnTip from '@/components/reused/ui/RouteBtnTip.vue'
  import AddStaticFileDialog from './AddStaticFileDialog.vue'
  import EditStaticFileDialog from './EditStaticFileDialog.vue'
  import ConfirmDialog from '@/components/reused/common/ConfirmDialog.vue'
  import GeneralError from '@/components/reused/common/GeneralError.vue'
  import LinkWithTip from '@/components/reused/ui/LinkWithTip.vue'
  import { mapActions, mapState } from 'vuex'
  import { STATIC_PAGES } from '@/constants/store.js'
  import { STATIC_FILE_TYPE_ENUM } from '@/constants/enums'
  import { STATIC_FILE_TYPES } from '@/constants/business'

  export default {
    components: {
      TextView,
      BtnIconTip,
      GeneralError,
      AddStaticFileDialog,
      EditStaticFileDialog,
      ConfirmDialog,
      LinkWithTip
    },
    data () {
      return {
        STATIC_FILE_TYPE_ENUM,
        isLoading: false,
        itemsPerPage: 20,
        addStaticFileDialog: {
          callback: null,
          show: null
        },
        editStaticFileDialog: {
          file: null,
          callback: null,
          show: null
        },
        confirmDialog: {
          show: false,
          additional: null,
          text: null,
          callback: null,
          warning: true
        },
        fileIcons: {
          html: 'mdi-language-html5',
          js: 'mdi-nodejs',
          json: 'mdi-code-json',
          md: 'mdi-language-markdown',
          pdf: 'mdi-file-pdf-box',
          png: 'mdi-file-image',
          ico: 'mdi-file-image',
          jpg: 'mdi-file-image',
          jpeg: 'mdi-file-image',
          txt: 'mdi-file-document-outline',
          xls: 'mdi-file-excel',
          xlsx: 'mdi-file-excel',
          docx: 'mdi-file-word',
          doc: 'mdi-file-word'
        }
      }
    },
    computed: {
      ...mapState(STATIC_PAGES.NAMESPACE, {
        items: state => (state[STATIC_PAGES.STATE.STATIC_FILES] || {})
      }),
      files () {
        return this.items.map(f => {
          const extension = f.fileName.substring(f.fileName.indexOf('.') + 1)

          return {
            ...f,
            icon: this.fileIcons[extension],
            canDelete: this.isMainAdmin ||
              (f.staticFileType !== STATIC_FILE_TYPES.PICTURE &&
                (this.isAdmin || this.isOrganizer)),
            canEdit: this.isAdmin || this.isOrganizer
          }
        })
      },
      headers1 () {
        return [
          { text: this.$t(''), align: 'start', sortable: true, value: 'icon' },
          { text: this.$t('Наименование'), align: 'start', sortable: true, value: 'fileName' },
          { text: this.$t('Тип файла'), align: 'start', sortable: false, value: 'staticFileType' },
          { text: this.$t('Тип содержимого'), align: 'start', sortable: false, value: 'contentType' },
          { text: this.$t('Дата загрузки'), align: 'start', sortable: false, value: 'creationDate' },
          { text: '', value: 'data-table-expand' }
        ]
      }

    },
    created () {
      const vm = this
      vm.clearStore().then(() => {
        vm.getStaticFiles()
      })
    },
    beforeDestroy () {
      this.clearStore()
    },
    methods: {
      ...mapActions(STATIC_PAGES.NAMESPACE, {
        getStaticFilesAction: STATIC_PAGES.ACTIONS.GET_STATIC_FILES,
        clearStaticFilesAction: STATIC_PAGES.ACTIONS.CLEAR_STATIC_FILES,
        deleteStaticFileAction: STATIC_PAGES.ACTIONS.DELETE_STATIC_FILE,
        uploadStaticFileAction: STATIC_PAGES.ACTIONS.UPLOAD_STATIC_FILE,
        editStaticFileAction: STATIC_PAGES.ACTIONS.EDIT_STATIC_FILE
      }),
      clearStore: async function () {
        await Promise.all(
          [
            this.clearStaticFilesAction()
          ])
      },
      openConfirmDialog: function (id) {
        this.confirmDialog.additional = id
        this.confirmDialog.text = this.$t('Файл будет удален. Продолжить?')
        this.confirmDialog.callback = this.deleteStaticFile
        this.confirmDialog.warning = true
        this.confirmDialog.show = true
      },
      openAddStaticFileDialog: function () {
        this.addStaticFileDialog.callback = this.getStaticFiles
        this.addStaticFileDialog.show = true
      },
      openEditStaticFileDialog: function (file) {
        this.editStaticFileDialog.file = file
        this.editStaticFileDialog.callback = this.getStaticFiles
        this.editStaticFileDialog.show = true
      },
      getStaticFiles: async function () {
        try {
          this.isLoading = true
          await this.getStaticFilesAction()
          this.isLoading = false
        } catch (error) {
          this.isLoading = false
          this.throwIfNotServerError(error)
        }
      },
      deleteStaticFile: async function (fileId) {
        try {
          this.isLoading = true
          await this.deleteStaticFileAction({
            fileId
          })
          await this.getStaticFiles()
          this.isLoading = false
        } catch (error) {
          this.isLoading = false
          this.throwIfNotServerError(error)
        }
      },
      copyLink: function (fileName) {
        this.copyToClipboard(this.getSystemFileUrl(fileName))
      },
      copyToClipboard: function (value) {
        if (window.clipboardData && window.clipboardData.setData) {
          // Internet Explorer-specific code path to prevent textarea being shown while dialog is visible.
          return window.clipboardData.setData('Text', value)
        } else if (document.queryCommandSupported && document.queryCommandSupported('copy')) {
          const textarea = document.createElement('textarea')
          textarea.textContent = value
          textarea.style.position = 'fixed' // Prevent scrolling to bottom of page in Microsoft Edge.
          document.body.appendChild(textarea)
          textarea.select()
          try {
            return document.execCommand('copy') // Security exception may be thrown by some browsers.
          } catch (ex) {
            console.warn('Copy to clipboard failed.', ex)
            return false
          } finally {
            document.body.removeChild(textarea)
          }
        }
      }
    }
  }
</script>
